import type { ReactNode } from 'react'
import type { ApiOrder } from '../api/types'
import type { CartLine } from '../helpers/cart'
import type { IconName } from './icons'
import { PAYMENT_METHOD, type PaymentMethod } from './settings'

export const ORDER_API_PATHS = {
  root: '/orders',
  item: (id: string) => `/orders/${encodeURIComponent(id)}`,
  cancel: (id: string) => `/orders/${encodeURIComponent(id)}/cancel`,
} as const

export const ORDER_PATHS = {
  list: '/profile/orders',
  nestedList: 'orders',
  nestedDetail: 'orders/:id',
  legacyList: '/orders',
  legacyDetail: 'orders/:id',
  checkout: '/checkout',
  detail: (id: string) => `/profile/orders/${id}`,
} as const

export const ORDER_STATUS = {
  pending: 'pending',
  confirmed: 'confirmed',
  preparing: 'preparing',
  outForDelivery: 'out_for_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
} as const

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

export const PAYMENT_STATUS = {
  unpaid: 'unpaid',
  paid: 'paid',
  refunded: 'refunded',
} as const

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS]

/** Customer can cancel until the order is packed and out for delivery. */
export const CANCELLABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  ORDER_STATUS.pending,
  ORDER_STATUS.confirmed,
  ORDER_STATUS.preparing,
]

/** Customers may cancel only during this window after the order is placed. */
export const ORDER_CANCEL_WINDOW_MS = 5 * 60 * 1000

/** Unpaid online orders are cancelled once this window passes. */
export const ONLINE_PAYMENT_WINDOW_MS = 5 * 60 * 1000

export const ONLINE_PAYMENT_EXPIRED =
  'Payment was not completed within 5 minutes'

export const ORDER_PROGRESS: readonly OrderStatus[] = [
  ORDER_STATUS.pending,
  ORDER_STATUS.confirmed,
  ORDER_STATUS.preparing,
  ORDER_STATUS.outForDelivery,
  ORDER_STATUS.delivered,
]

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  [ORDER_STATUS.pending]: 'Pending',
  [ORDER_STATUS.confirmed]: 'Confirmed',
  [ORDER_STATUS.preparing]: 'Preparing',
  [ORDER_STATUS.outForDelivery]: 'Out for delivery',
  [ORDER_STATUS.delivered]: 'Delivered',
  [ORDER_STATUS.cancelled]: 'Cancelled',
}

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  [PAYMENT_STATUS.unpaid]: 'Unpaid',
  [PAYMENT_STATUS.paid]: 'Paid',
  [PAYMENT_STATUS.refunded]: 'Refunded',
}

export const ORDER_LIST_LIMIT = 50

export const ORDER_FILTER = {
  all: 'all',
  active: 'active',
  delivered: 'delivered',
  cancelled: 'cancelled',
} as const

export type OrderFilter = (typeof ORDER_FILTER)[keyof typeof ORDER_FILTER]

export const ORDER_FILTER_STATUSES: Record<
  Exclude<OrderFilter, typeof ORDER_FILTER.all>,
  readonly OrderStatus[]
> = {
  [ORDER_FILTER.active]: [
    ORDER_STATUS.pending,
    ORDER_STATUS.confirmed,
    ORDER_STATUS.preparing,
    ORDER_STATUS.outForDelivery,
  ],
  [ORDER_FILTER.delivered]: [ORDER_STATUS.delivered],
  [ORDER_FILTER.cancelled]: [ORDER_STATUS.cancelled],
}

export const ORDER_FILTER_OPTIONS: readonly { id: OrderFilter; label: string }[] = [
  { id: ORDER_FILTER.all, label: 'All' },
  { id: ORDER_FILTER.active, label: 'Active' },
  { id: ORDER_FILTER.delivered, label: 'Delivered' },
  { id: ORDER_FILTER.cancelled, label: 'Cancelled' },
]

export const ORDER_FILTER_PARAMS = {
  query: 'q',
  status: 'status',
} as const

export type OrderCardProps = {
  order: ApiOrder
  cancelling?: boolean
  onCancel?: (id: string) => void
}

export type OrderPlacedState = {
  placed: true
  checkoutId?: string | null
  orderIds?: string[]
}

export const ORDER_PLACED_STATE: OrderPlacedState = {
  placed: true,
}

export const ORDER_UI = {
  listTitle: 'Your orders',
  listLead: 'Track delivery and manage your recent orders.',
  listCountSingular: 'order',
  listCountPlural: 'orders',
  listCountOf: 'of',
  searchLabel: 'Search orders',
  searchPlaceholder: 'Search by order number, item or recipient',
  filterLabel: 'Filter orders by status',
  noMatchTitle: 'No matching orders',
  noMatchHint: 'Try a different search or status.',
  clearFilters: 'Clear filters',
  emptyTitle: 'No orders yet',
  emptyHint: 'When you place an order, it will show up here.',
  emptyLoginTitle: 'Sign in to track your orders',
  emptyLoginHint: 'Order history is saved to your account.',
  loginCta: 'Sign in',
  continueShopping: 'Continue shopping',
  viewOrder: 'View details',
  cancel: 'Cancel order',
  cancelling: 'Cancelling…',
  cancelConfirm:
    'Cancel this order? You can cancel within 5 minutes of placing it.',
  cancelReasonLabel: 'Reason (optional)',
  cancelReasonPlaceholder: 'Tell us why you are cancelling',
  loading: 'Loading orders…',
  loadingAria: 'Loading orders',
  loadError: 'Could not load orders',
  detailTitle: 'Order',
  backToOrders: 'Back to orders',
  placedTitle: 'Thank you — your order is in',
  placedHint: 'This order is saved to your account. Track it anytime from Profile → Orders.',
  placedSplitHint:
    'We split this checkout into two orders so 60-minute items can still arrive fast.',
  placedPayHint: 'Complete payment to confirm this order.',
  payNow: 'Pay now',
  paying: 'Opening payment…',
  paymentError: 'Could not complete payment. Try Pay now again.',
  paymentExpired:
    'This order was cancelled because payment was not completed within 5 minutes.',
  siblingHeading: 'Also placed in this checkout',
  itemsHeading: 'Items',
  deliveryHeading: 'Shipping',
  billingHeading: 'Billing',
  billingSameAsShipping: 'Same as shipping address',
  summaryHeading: 'Summary',
  subtotalLabel: 'Subtotal',
  discountLabel: 'Discount',
  deliveryFeeLabel: 'Delivery',
  totalLabel: 'Total',
  placedOn: 'Placed',
  recipientLabel: 'Recipient',
  phoneLabel: 'Phone',
  addressLabel: 'Address',
  notesLabel: 'Notes',
  statusLabel: 'Status',
  paymentLabel: 'Payment',
  deliveryPromiseLabel: 'Delivery',
  scheduledLabel: 'Scheduled for',
  expressBadge: '60 min',
  expressPromise: '60-minute delivery',
  standardPromise: 'Standard delivery',
  notFound: 'This order could not be found.',
  headerLabel: 'Orders',
} as const

export const CHECKOUT_STEP = {
  cart: 'cart',
  address: 'address',
  checkout: 'checkout',
  done: 'done',
} as const

export type CheckoutStep = (typeof CHECKOUT_STEP)[keyof typeof CHECKOUT_STEP]

export const CHECKOUT_STEPS: readonly { id: CheckoutStep; label: string }[] = [
  { id: CHECKOUT_STEP.cart, label: 'Cart' },
  { id: CHECKOUT_STEP.address, label: 'Address' },
  { id: CHECKOUT_STEP.checkout, label: 'Checkout' },
  { id: CHECKOUT_STEP.done, label: 'Done' },
]

export type CheckoutAddonOption = {
  id: string
  label: string
  price: number
  imageUrl: string | null
}

export type CheckoutLineAddons = {
  lineId: string
  options: CheckoutAddonOption[]
}

export type CheckoutShipmentGroup = {
  mode: string
  title: string
  hint: string
  lines: CartLine[]
}

export const CHECKOUT_UI = {
  pageTitle: 'Checkout',
  addressTitle: 'Delivery address',
  addressLead:
    'The delivery area is fixed for this order. Add the recipient, then continue to payment.',
  barTitle: 'Secure checkout',
  barLead: 'Stay on this page until payment is complete.',
  checkoutLead: 'Delivery time is based on the address you just entered.',
  deliverTo: 'Delivering to',
  changeAddress: 'Edit address',
  paymentCompactLead: 'Payment',
  doneTitle: 'Order placed',
  emptyTitle: 'Your cart is empty',
  emptyHint: 'Add something to your cart before checking out.',
  emptyLoginTitle: 'Sign in to place your order',
  emptyLoginHint: 'Checkout needs an account so we can save the order.',
  loginCta: 'Sign in to checkout',
  continueShopping: 'Continue shopping',
  continueToCheckout: 'Continue to payment',
  payNow: 'Pay now',
  deliveryHeading: 'Shipping address',
  deliveryLead: 'Where should we deliver this order?',
  billingHeading: 'Billing address',
  billingLead: 'Required for the invoice. You can use the shipping address.',
  billingSameAsShipping: 'Billing address is the same as shipping',
  billingName: 'Billing name',
  billingPhone: 'Billing phone',
  billingAddress: 'Billing address',
  recipientName: 'Recipient name',
  recipientPhone: 'Recipient phone',
  deliveryArea: 'Delivery area',
  address: 'House, flat, street',
  city: 'City',
  pincode: 'Pincode',
  pincodePlaceholder: '6-digit pincode',
  pincodeManualHint: 'We couldn’t detect the pincode for this area. Please enter it.',
  pincodeResolving: 'Finding pincode…',
  pincodeInvalid: 'Enter a valid 6-digit pincode for this delivery area.',
  notes: 'Gift message or instructions',
  notesPlaceholder: 'Optional',
  locationMissing:
    'Delivery area is missing. Go back and choose an area, then return to checkout.',
  changeLocation: 'Change location',
  placeOrder: 'Place order',
  placing: 'Placing your order…',
  placingOnline: 'Opening payment…',
  placingTitle: 'Placing your order',
  placingLead: 'Please wait while we confirm delivery and payment.',
  confirmTitle: 'Place this order?',
  confirmLead: 'We will use the address, delivery plan, and payment method you selected.',
  confirmPlace: 'Yes, place order',
  confirmCancel: 'Review order',
  closeAria: 'Close',
  confirmDialogLabel: 'Confirm your order',
  itemsHeading: 'Order summary',
  addonsHeading: 'Add a gift',
  subtotalLabel: 'Subtotal',
  totalLabel: 'Total',
  validationError:
    'Please fill shipping and billing name, a 10-digit phone, address, city, and pincode.',
  placeError: 'Could not place the order.',
  paymentHeading: 'Payment method',
  paymentLead: 'Choose how you want to pay.',
  paymentCod: 'Cash on delivery',
  paymentOnline: 'Pay online',
  paymentCodHint: 'Pay in cash when the order is delivered.',
  paymentOnlineHint: 'Pay securely with UPI, cards, or netbanking.',
  paymentOnlineNote: 'A secure payment window will open so you can pay this checkout.',
  paymentScriptError: 'Could not load the payment window. Check your connection and try again.',
  paymentMissing: 'Online payment is not available for this order yet.',
  couponLabel: 'Coupon code',
  couponPlaceholder: 'Enter code',
  couponApply: 'Apply',
  couponEmpty: 'Enter a coupon code.',
  couponInvalid: 'This coupon isn’t valid.',
  successEyebrow: 'Order confirmed',
  successTitle: 'Thank you for your order',
  successLead: 'Your order is saved to your account. Track delivery anytime from Orders.',
  successSplitLead:
    'We split this into two orders so 60-minute items can still arrive fast.',
  successOrderLabel: 'Order',
  viewOrder: 'View details',
  paymentLabel: 'Payment',
} as const

/** How long checkout waits for the backend to settle online payment. */
export const CHECKOUT_PAYMENT_CONFIRM = {
  timeoutMs: 5_000,
  intervalMs: 1_000,
} as const

export const CHECKOUT_PLACING_PHASE = {
  saving: 'saving',
  paying: 'paying',
  confirming: 'confirming',
} as const

export type CheckoutPlacingPhase =
  (typeof CHECKOUT_PLACING_PHASE)[keyof typeof CHECKOUT_PLACING_PHASE]

export const CHECKOUT_PLACING_STEPS = [
  { id: CHECKOUT_PLACING_PHASE.saving, label: 'Placing your order' },
  { id: CHECKOUT_PLACING_PHASE.paying, label: 'Complete your payment' },
  { id: CHECKOUT_PLACING_PHASE.confirming, label: 'Confirming your payment' },
] as const

export const CHECKOUT_PLACING_COPY: Record<
  CheckoutPlacingPhase,
  { title: string; lead: string }
> = {
  saving: {
    title: 'Placing your order',
    lead: 'This only takes a moment.',
  },
  paying: {
    title: 'Complete your payment',
    lead: 'A secure payment window is opening.',
  },
  confirming: {
    title: 'Confirming your payment',
    lead: 'We’re checking that your payment went through.',
  },
}

export const CHECKOUT_OUTCOME = {
  paid: 'paid',
  cod: 'cod',
  pending: 'pending',
  refunded: 'refunded',
  cancelled: 'cancelled',
} as const

export type CheckoutOutcome = (typeof CHECKOUT_OUTCOME)[keyof typeof CHECKOUT_OUTCOME]

export const CHECKOUT_OUTCOME_COPY: Record<
  CheckoutOutcome,
  { eyebrow: string; title: string; lead: string; mark: IconName }
> = {
  paid: {
    eyebrow: 'Payment received',
    title: 'Thank you for your order',
    lead: 'Payment is confirmed. Track delivery anytime from Orders.',
    mark: 'check',
  },
  cod: {
    eyebrow: 'Order placed',
    title: 'Thank you for your order',
    lead: 'Pay in cash when the order is delivered. Track it anytime from Orders.',
    mark: 'check',
  },
  pending: {
    eyebrow: 'Payment not finished',
    title: 'Complete your payment',
    lead: 'Your order is placed. Pay within 5 minutes from the order page, or it will be cancelled.',
    mark: 'alert',
  },
  cancelled: {
    eyebrow: 'Order cancelled',
    title: 'Payment was not completed',
    lead: 'We cancelled this order because payment was not finished within 5 minutes.',
    mark: 'alert',
  },
  refunded: {
    eyebrow: 'Payment refunded',
    title: 'This payment was refunded',
    lead: 'The order is saved. Open it for the latest payment status.',
    mark: 'undo',
  },
}

export type CheckoutStepperProps = {
  current: CheckoutStep
  addressComplete?: boolean
  placed?: boolean
  onSelect: (step: CheckoutStep) => void
}

export type CheckoutHeaderProps = CheckoutStepperProps & {
  title: string
  lead: string
}

export type CheckoutDialogProps = {
  title: string
  lead: string
  cancelLabel?: string
  confirmLabel?: string
  confirming?: boolean
  confirmBusyLabel?: string
  labelledBy: string
  describedBy: string
  dialogLabel: string
  closeAria: string
  dismissible?: boolean
  marked?: boolean
  onCancel?: () => void
  onConfirm?: () => void
  children?: ReactNode
}

export type CheckoutPlacingProps = {
  phase: CheckoutPlacingPhase
  online: boolean
}

export type CheckoutSuccessProps = {
  orders: ApiOrder[]
  split: boolean
}

export type CheckoutPaymentChoice = typeof PAYMENT_METHOD.cod | typeof PAYMENT_METHOD.online

export type CheckoutPaymentOption = {
  value: CheckoutPaymentChoice
  label: string
  hint: string
}

export type CheckoutFormState = {
  recipientName: string
  recipientPhone: string
  address: string
  pincode: string
  notes: string
  paymentMethod: PaymentMethod
  billingSameAsShipping: boolean
  billingName: string
  billingPhone: string
  billingAddress: string
  billingCity: string
  billingPincode: string
}

export const CHECKOUT_PAYMENT_OPTIONS: readonly CheckoutPaymentOption[] = [
  {
    value: PAYMENT_METHOD.cod,
    label: CHECKOUT_UI.paymentCod,
    hint: CHECKOUT_UI.paymentCodHint,
  },
  {
    value: PAYMENT_METHOD.online,
    label: CHECKOUT_UI.paymentOnline,
    hint: CHECKOUT_UI.paymentOnlineHint,
  },
]
